import React from "react";
import { Flex, Text } from "rebass/styled-components";
import {
  FaUser,
  FaPlus,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import { IPlayer, ISuggestedMoment } from "../../types";
import {
  WizardOverlay,
  WizardContainer,
  WizardStep,
  WizardTitle,
  WizardSubtitle,
  WizardTeamsContainer,
  WizardTeamColumn,
  WizardTeamHeader,
  WizardPlayersGrid,
  WizardPlayerCard,
  WizardPlayerAvatar,
  WizardPlayerAvatarImg,
  WizardPlayerInfo,
  WizardPlayerName,
  WizardPlayerStats,
  WizardPlayerAction,
  WizardSkipButton,
  WizardMomentsSection,
  WizardMomentsTitle,
  WizardMomentsList,
  WizardMomentCard,
  WizardContinueButton,
  YouBadge,
  SuggestionInfo,
  SuggestionLabel,
  SuggestionMeta,
  SuggestionAddIcon,
} from "../../styledComponents";

interface IPlayerStats {
  kills: number;
  deaths: number;
}

export interface IPlayerSelectorWizardProps {
  wizardStep: "player" | "moments" | "editor";
  setWizardStep: (step: "player" | "moments" | "editor") => void;
  sortedPlayers: IPlayer[];
  ctPlayerCount: number;
  currentUserSteamId: string | null;
  selectedPlayers: Set<string>;
  wizardSelectedPlayer: string | null;
  playerStats: Record<string, IPlayerStats>;
  getPlayerName: (steamId: string) => string;
  handleWizardSelectPlayer: (playerId: string) => void;
  wizardPlayerMoments: ISuggestedMoment[];
  addedMomentIds: Set<string>;
  handleWizardAddMoment: (moment: ISuggestedMoment) => void;
  handleWizardComplete: () => void;
  tickRate: number;
}

export const PlayerSelectorWizard: React.FC<IPlayerSelectorWizardProps> = ({
  wizardStep,
  setWizardStep,
  sortedPlayers,
  ctPlayerCount,
  currentUserSteamId,
  selectedPlayers,
  wizardSelectedPlayer,
  playerStats,
  getPlayerName,
  handleWizardSelectPlayer,
  wizardPlayerMoments,
  addedMomentIds,
  handleWizardAddMoment,
  handleWizardComplete,
  tickRate,
}) => {
  if (wizardStep === "editor") return null;

  const formatDuration = (startTick: number, endTick: number): string => {
    const durationSecs = Math.round((endTick - startTick) / tickRate);
    return `${durationSecs}s`;
  };

  if (wizardStep === "player") {
    const ctPlayers = sortedPlayers.slice(0, ctPlayerCount);
    const tPlayers = sortedPlayers.slice(ctPlayerCount);

    const sortByCurrentUser = (players: IPlayer[]) =>
      [...players].sort((a, b) => {
        const aIsCurrentUser = a.steamId === currentUserSteamId;
        const bIsCurrentUser = b.steamId === currentUserSteamId;
        if (aIsCurrentUser && !bIsCurrentUser) return -1;
        if (!aIsCurrentUser && bIsCurrentUser) return 1;
        return 0;
      });

    const renderPlayerCard = (player: IPlayer, team: "ct" | "t") => {
      const isCurrentUser = player.steamId === currentUserSteamId;
      const isAlreadySelected = selectedPlayers.has(player.steamId);
      const stats = playerStats[player.steamId];

      return (
        <WizardPlayerCard
          key={player.steamId}
          $team={team}
          $isCurrentUser={isCurrentUser}
          $selected={
            isAlreadySelected || wizardSelectedPlayer === player.steamId
          }
          onClick={() =>
            !isAlreadySelected && handleWizardSelectPlayer(player.steamId)
          }
          style={isAlreadySelected ? { opacity: 0.5, cursor: "default" } : {}}
        >
          {player.avatar ? (
            <WizardPlayerAvatarImg src={player.avatar} alt={player.name} />
          ) : (
            <WizardPlayerAvatar $team={team} $isCurrentUser={isCurrentUser}>
              <FaUser size={16} />
            </WizardPlayerAvatar>
          )}
          <WizardPlayerInfo>
            <WizardPlayerName $isCurrentUser={isCurrentUser}>
              {getPlayerName(player.steamId)}
              {isCurrentUser && <YouBadge>YOU</YouBadge>}
            </WizardPlayerName>
            <WizardPlayerStats>
              <span className="kills">{stats?.kills || 0} K</span>
              <span className="deaths">{stats?.deaths || 0} D</span>
            </WizardPlayerStats>
          </WizardPlayerInfo>
          <WizardPlayerAction $isAdded={isAlreadySelected}>
            {isAlreadySelected ? <FaCheck size={10} /> : <FaPlus size={10} />}
          </WizardPlayerAction>
        </WizardPlayerCard>
      );
    };

    return (
      <WizardOverlay>
        <WizardContainer>
          <WizardStep>Step 1 of 2</WizardStep>
          <WizardTitle>Select a Player</WizardTitle>
          <WizardSubtitle>
            Choose whose moments you want to capture. We'll show you their best
            moments from this match.
          </WizardSubtitle>

          <WizardTeamsContainer>
            <WizardTeamColumn $team="ct">
              <WizardTeamHeader $team="ct">
                Team 1 (CT-side start)
              </WizardTeamHeader>
              <WizardPlayersGrid>
                {sortByCurrentUser(ctPlayers).map((player) =>
                  renderPlayerCard(player, "ct"),
                )}
              </WizardPlayersGrid>
            </WizardTeamColumn>

            <WizardTeamColumn $team="t">
              <WizardTeamHeader $team="t">
                Team 2 (T-side start)
              </WizardTeamHeader>
              <WizardPlayersGrid>
                {sortByCurrentUser(tPlayers).map((player) =>
                  renderPlayerCard(player, "t"),
                )}
              </WizardPlayersGrid>
            </WizardTeamColumn>
          </WizardTeamsContainer>

          <WizardSkipButton onClick={() => setWizardStep("editor")}>
            Close
          </WizardSkipButton>
        </WizardContainer>
      </WizardOverlay>
    );
  }

  if (wizardStep === "moments") {
    const selectedCount = wizardPlayerMoments.filter((m) =>
      addedMomentIds.has(m.id),
    ).length;

    return (
      <WizardOverlay>
        <WizardContainer>
          <WizardStep>Step 2 of 2</WizardStep>
          <WizardTitle>Select Highlights</WizardTitle>
          <WizardSubtitle>
            These are the best moments we found. Click to add them to your clip.
          </WizardSubtitle>

          {wizardPlayerMoments.length > 0 ? (
            <WizardMomentsSection>
              <WizardMomentsTitle>
                Detected Highlights ({wizardPlayerMoments.length})
              </WizardMomentsTitle>
              <WizardMomentsList>
                {wizardPlayerMoments.map((moment) => {
                  const isAdded = addedMomentIds.has(moment.id);
                  return (
                    <WizardMomentCard
                      key={moment.id}
                      $momentType={moment.type}
                      $isAdded={isAdded}
                      onClick={() => handleWizardAddMoment(moment)}
                    >
                      <SuggestionInfo>
                        <SuggestionLabel $momentType={moment.type}>
                          {moment.kills[0]?.killerData?.weapon || ""}{" "}
                          {moment.label}
                        </SuggestionLabel>
                        <SuggestionMeta>
                          <span>Round {moment.round}</span>
                          {moment.headshotCount > 0 && (
                            <>
                              <span className="separator">·</span>
                              <span>{moment.headshotCount} headshots</span>
                            </>
                          )}
                        </SuggestionMeta>
                      </SuggestionInfo>
                      <SuggestionAddIcon $isAdded={isAdded}>
                        {isAdded ? <FaCheck size={10} /> : <FaPlus size={10} />}
                      </SuggestionAddIcon>
                    </WizardMomentCard>
                  );
                })}
              </WizardMomentsList>
              <Text
                fontSize="12px"
                color="#557077"
                textAlign="center"
                style={{ marginTop: "12px" }}
              >
                {selectedCount === 0
                  ? "Click to select highlights"
                  : `${selectedCount} of ${wizardPlayerMoments.length} selected`}
              </Text>
            </WizardMomentsSection>
          ) : (
            <Text
              fontSize="14px"
              color="#557077"
              textAlign="center"
              style={{ padding: "40px 20px" }}
            >
              No multi-kills found for this player. You can still create custom
              segments in the editor.
            </Text>
          )}

          <Flex
            style={{
              gap: "12px",
              marginTop: "24px",
              justifyContent: "center",
            }}
          >
            <WizardSkipButton
              onClick={() => setWizardStep("player")}
              style={{ marginTop: 0 }}
            >
              <FaChevronLeft size={10} />
              Back
            </WizardSkipButton>
            <WizardContinueButton onClick={handleWizardComplete}>
              {(() => {
                const addedForPlayer = wizardPlayerMoments.filter((m) =>
                  addedMomentIds.has(m.id),
                ).length;
                if (addedForPlayer > 0) {
                  return `Continue with ${addedForPlayer} moment${
                    addedForPlayer > 1 ? "s" : ""
                  }`;
                }
                return wizardPlayerMoments.length > 0
                  ? "Skip highlights"
                  : "Continue to Editor";
              })()}
              <FaChevronRight size={12} />
            </WizardContinueButton>
          </Flex>
        </WizardContainer>
      </WizardOverlay>
    );
  }

  return null;
};

export default PlayerSelectorWizard;
